import { useSelector } from '@xstate/store-react';

import 'moment/locale/en-gb';
import 'moment/locale/pl';

import type { CSSProperties, ReactNode, Ref } from 'react';
import type { PinContentFn } from '../components/Sidebar/PinContent';
import type { MenuItemChildProps } from '../components/Sidebar/SectionMenuItemChild';
import type {
  AnnouncementLocalStorage,
  ImpersonateLocalStorage,
} from '../types/localStorage';

import { use, useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Link, Outlet } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';

import { getEnv } from '../lib/env';
import {
  getHash,
  getImportantResourceNames,
  getTestProps,
  updateMomentLocale,
} from '../lib/helpers';
import { pageStore } from '../lib/store/pageStore';

import AnnouncementComponent from '../components/Announcement/AnnouncementComponent';
import { useGridNavigate } from '../components/DataGrid/useGridFilters';
import ImpersonateCard from '../components/ImpersonateCard/ImpersonateCard';
import Sidebar from '../components/Sidebar/Sidebar';

import useDarkMode from '../hooks/useDarkMode';
import useSelectedSpace from '../hooks/useSelectedSpace';
import { useSpaceLimits } from '../hooks/useSpaceLimits';
import { useHelpCenterMenuItems } from './hooks/useHelpCenterMenuItems';
import { useUserMenuItems } from './hooks/useUserMenuItems';

import UserContext from '../contexts/UserContext';

import { FlotiqLogo, FlotiqLogoWhite } from '../images/shapes';

const USAGE_FIELDS = getImportantResourceNames();

type StructureProps = {
  /**
   * Reference to the root div element
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Link to the logo
   */
  logoLink?: string;
  /**
   * Additional element to be rendered
   */
  additionalElement?: ReactNode;
  /**
   * Component testId
   */
  testId?: string;
  /**
   * Content to be rendered inside the layout
   */
  children?: ReactNode;
  /**
   * Additional classes to be added to the layout
   */
  additionalClasses?: string;
  /**
   * Sidebar menu items
   */
  sidebarMenuItems?: MenuItemChildProps[];
  /**
   * Callback function to pin content
   */
  pinContentCallback?: PinContentFn;
  /**
   * Flag to show upgrade option
   */
  showUpgrade?: boolean;
};

const Structure = ({
  ref,
  logoLink,
  additionalElement,
  testId,
  additionalClasses,
  children,
  sidebarMenuItems,
  pinContentCallback,
  showUpgrade,
}: StructureProps) => {
  const { t, i18n } = useTranslation();
  const { userData, isAdmin } = use(UserContext);

  const [impersonate, , { removeItem: removeImpersonation }] =
    useSessionStorageState<ImpersonateLocalStorage>('cms.impersonate');
  const [announcementData, setAnnouncementData] =
    useLocalStorageState<AnnouncementLocalStorage>('cms.user_hideAnnouncement');

  const { space, spaceLimits } = useSelectedSpace();
  const [darkMode] = useDarkMode();
  const isFree = spaceLimits?.price === 0;

  const { navigateGrid } = useGridNavigate('all-users', '/users-data-preview');

  useEffect(() => {
    const userLang = userData?.language;
    if (userLang && userLang !== i18n.language) {
      i18n.changeLanguage(userLang);
    }
  }, [i18n, userData?.language]);

  useEffect(() => {
    updateMomentLocale(i18n.language);
  }, [i18n.language]);

  const endImpersonation = useCallback(() => {
    removeImpersonation();
    navigateGrid();
  }, [navigateGrid, removeImpersonation]);

  useEffect(() => {
    const handleUnload = () => {
      removeImpersonation();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [removeImpersonation]);

  const [sidebarOpen, setSidebarOpen] = useLocalStorageState(
    'cms.user_sideBarOpen',
  );

  const announcement = getEnv('VITE_ANNOUNCEMENT');

  const announcementHash = useMemo(() => {
    return getHash(announcement);
  }, [announcement]);

  const hideAnnouncement = useMemo(() => {
    if (!announcement) return true;
    if (announcementData?.id === announcementHash) return announcementData.hide;
    return false;
  }, [announcement, announcementData, announcementHash]);

  const handleCloseAnnouncement = useCallback(() => {
    setAnnouncementData({
      id: announcementHash,
      hide: true,
    });
  }, [announcementHash, setAnnouncementData]);

  const isSizeSM = useMediaQuery({
    query: '(max-width: 1024px)',
  });

  const sidebarOpened =
    typeof sidebarOpen === 'boolean' ? sidebarOpen : !isSizeSM;

  const handleSidebar = useCallback(
    (value: boolean) => {
      setSidebarOpen(value);
    },
    [setSidebarOpen],
  );

  const { userMenuItems } = useUserMenuItems();
  const { helpCenterMenuItems } = useHelpCenterMenuItems();
  const page = useSelector(pageStore, (state) => state.context.page);
  const menuItemOpen = useSelector(
    pageStore,
    (state) => state.context.menuItemOpen,
  );
  const hideSidebar = useSelector(
    pageStore,
    (state) => state.context.hideSidebar,
  );

  const sidebarItems = useMemo(
    () => [...(sidebarMenuItems || []), ...helpCenterMenuItems],
    [sidebarMenuItems, helpCenterMenuItems],
  );

  const { data: accountResources } = useSpaceLimits(spaceLimits, USAGE_FIELDS);

  const isLimitReached = useMemo(() => {
    if (!isFree) return false;
    return (
      accountResources.filter(
        ({ percent, limit }) =>
          percent >= 100 && typeof limit === 'number' && limit > 0,
      )?.length > 0
    );
  }, [accountResources, isFree]);

  const [layoutPadding, layoutOffset] = useMemo(() => {
    if (hideAnnouncement && isLimitReached) return ['pt-12', '48px'];
    if (!isLimitReached && !hideAnnouncement) return ['pt-16', '64px'];
    if (isLimitReached && !hideAnnouncement) return ['pt-28', '112px'];
    return ['', '0px'];
  }, [hideAnnouncement, isLimitReached]);

  const { firstName, lastName, email } = userData || {
    firstName: '',
    lastName: '',
    email: '',
  };

  return (
    <>
      <AnnouncementComponent
        additionalClasses="h-16"
        announcement={announcement}
        handleClose={handleCloseAnnouncement}
        hasCloseButton={true}
        hidden={hideAnnouncement}
        {...getTestProps(testId, 'announcement', 'testId')}
      />
      {isLimitReached && (
        <AnnouncementComponent
          announcement={
            <>
              {t('Limits.FreePlanLimitReached')}{' '}
              {isAdmin ? (
                <Trans i18nKey="Limits.FreePlanUpgradeAdmin">
                  <Link
                    className="inline mx-1 underline hover:text-red-100 active:text-white focus:text-white"
                    to={`/space/upgrade/${space}`}
                  >
                    Upgrade
                  </Link>
                  now to keep everything running smoothly.
                </Trans>
              ) : (
                t('Limits.FreePlanUpgradeUser')
              )}
            </>
          }
          hasCloseButton={false}
          additionalClasses={twMerge(
            'inline sm:flex lg:text-lg p-1 bg-red h-12 text-white font-bold',
            !hideAnnouncement && 'top-16',
          )}
          {...getTestProps(testId, 'upgrade-plan', 'testId')}
        />
      )}

      <main
        ref={ref}
        className={twMerge(
          'flex items-start justify-between brand-bg-gradient w-full',
          'dark:bg-slate-900!',
          'min-h-screen md:pb-0',
          'transition-all',
          'ease-in-out',
          'duration-normal relative ',
          sidebarOpened
            ? 'pl-0 sm:pl-[68px] lg:pl-[240px]'
            : 'pl-0 sm:pl-[68px]',
          hideSidebar && 'sm:pl-0 lg:pl-0',
          layoutPadding,
          additionalClasses,
        )}
        style={{ '--flotiqLayoutOffset': layoutOffset } as CSSProperties}
        {...getTestProps(testId)}
      >
        {additionalElement}
        {!hideSidebar && (
          <Sidebar
            showUpgrade={showUpgrade && isFree}
            additionalContainerClasses={twMerge(
              'fixed top-0 bottom-0 left-0 z-20',
              layoutPadding,
            )}
            logo={
              darkMode ? (
                <FlotiqLogoWhite className="h-10" />
              ) : (
                <FlotiqLogo className="h-10" />
              )
            }
            logoLink={logoLink || '/'}
            menuItems={sidebarItems}
            logoConfig={{
              height: 64,
              marginBottom: showUpgrade && isFree ? 16 : 36,
              marginTop: 0,
            }}
            upgradeConfig={
              showUpgrade && isFree
                ? { height: 32, marginBottom: 20, marginTop: 0 }
                : undefined
            }
            menuConfig={{
              selected: page,
              openItem: menuItemOpen,
            }}
            userConfig={{
              name: `${firstName} ${lastName}`,
              email: email,
              height: 64,
              isModalOnClose: true,
              isModalOnCloseOutside: true,
              hasEventOnCloseOutside: true,
              menuItem: userMenuItems,
            }}
            isOpen={sidebarOpened}
            handleSidebar={handleSidebar}
            pinContentCallback={pinContentCallback}
            additionalUserEmailClasses={
              'wrap-break-word leading-normal whitespace-normal mr-2 pb-0.5 line-clamp-2'
            }
            {...getTestProps(testId, 'sidebar', 'testId')}
          />
        )}
        {children || <Outlet />}
        {impersonate && (
          <ImpersonateCard
            userInitials={firstName?.[0] + lastName?.[0] || 'N/A'}
            email={impersonate.email}
            onClose={endImpersonation}
            {...getTestProps(testId, 'impersonate-card', 'testId')}
          />
        )}
      </main>
    </>
  );
};

export default Structure;
